import { HoverCard, Stack, Text, Tooltip } from "@mantine/core";
import { MessageSquare } from "lucide-react";

import { Linkify } from "./Linkify";

/**
 * US-86.2: отображение комментария к ячейке дня в очереди согласования.
 *
 * Два режима — намеренно разнесены, чтобы откат был тривиальным:
 *  - "hovercard" (US-86.1): навёл → карточка, заводишь мышь внутрь → кликаешь
 *     Jira-ссылку, ушёл → закрылась. Интерактивно.
 *  - "tooltip" (прежнее поведение до US-86): чёрный тултип-превью по
 *     наведению, БЕЗ кликабельных ссылок.
 *
 * Режим по умолчанию зависит от СТЕНДА (см. resolveDefaultMode):
 *  - skedd-test (dev) → "hovercard" — пользовательница продолжает оценивать
 *    карточку на стенде;
 *  - прод (skedd.devsun.ru) и всё прочее → "tooltip" (решение 2026-06-03) —
 *    превью-тултип симпатичнее; кликабельные ссылки остаются внизу
 *    («Распределение по проектам» + панель «Комментарии»).
 * Обе реализации сохранены. Вернуть карточку и на прод = `return "hovercard"`
 * в resolveDefaultMode (или передать prop `mode` в конкретном месте).
 */
export type CommentHintMode = "hovercard" | "tooltip";

function resolveDefaultMode(): CommentHintMode {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return host.includes("skedd-test") ? "hovercard" : "tooltip";
}

interface CellCommentHintProps {
  comments: string[];
  /** Цвет иконки 💬 (обычно n.textMuted). */
  iconColor: string;
  /** data-testid на иконке — для тестов очереди. */
  testId?: string;
  /** Явно задать режим; по умолчанию — по стенду (resolveDefaultMode). */
  mode?: CommentHintMode;
}

export function CellCommentHint({
  comments,
  iconColor,
  testId,
  mode,
}: CellCommentHintProps) {
  if (comments.length === 0) return null;

  const resolvedMode = mode ?? resolveDefaultMode();

  const icon = (
    <span
      data-testid={testId}
      style={{
        marginLeft: 4,
        verticalAlign: "middle",
        display: "inline-flex",
        cursor: resolvedMode === "hovercard" ? "pointer" : "help",
      }}
    >
      <MessageSquare size={11} style={{ color: iconColor }} />
    </span>
  );

  if (resolvedMode === "tooltip") {
    return (
      <Tooltip
        label={comments.join("\n")}
        multiline
        withArrow
        position="top"
        maw={320}
        openDelay={200}
      >
        {icon}
      </Tooltip>
    );
  }

  return (
    <HoverCard width={320} position="top" withArrow shadow="md" openDelay={120} closeDelay={150}>
      <HoverCard.Target>{icon}</HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap={6}>
          {comments.map((c, ci) => (
            <Text key={ci} size="xs" style={{ wordBreak: "break-word" }}>
              <Linkify text={c} />
            </Text>
          ))}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
